import { authedFetch } from "../../utils/authedFetch";
import { readErrorBody } from "../../utils/readErrorBody";
import { Fragment, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestored } from "../../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Loader2, Mail, ChevronDown, Check, Download, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  deleteContactFromFirebase,
  getAllContactsFromFirebase,
  loadCurrentUser,
  updateUserProfile,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import DeleteRow from "../reuseable/confirm";
import { actionType } from "../../context/reducer";
import ContactDrawer from "./ContactDrawer";
import ContactEditDrawer from "./ContactEditDrawer";
import FubImportModal from "./FubImportModal";
import PageHeader from "../PageHeader";
import { addContact, updateContact } from "../../utils/contactsApi";
import {
  loadContactGroupsFromFirestore,
  syncContactGroupsFromGoogle,
  updateContactMemberships,
} from "../../utils/contactGroups";
import { SkeletonRow } from "../Skeleton";
import {
  SEGMENTS,
  enrichContacts,
  filterBySegment,
  countBySegment,
} from "../../utils/contactCrm";
import { formatDate } from "../../utils/helper";

const SEGMENT_BADGE = {
  client: "bg-emerald-50 text-emerald-700",
  past: "bg-slate-100 text-slate-600",
  lead: "bg-blue-50 text-blue-700",
  other: "bg-gray-50 text-gray-400",
};
const SEGMENT_LABEL = {
  client: "Client",
  past: "Past client",
  lead: "Lead",
  other: "-",
};

function MainNewContact() {
  const [{ user, users, steps, deals, buyerLeads, listingLeads }, dispatch] =
    useStateValue();
  const navigate = useNavigate();
  const gmailConnected = Boolean(user?.gmailtoken?.access_token);

  const [firebaseContacts, setFirebaseContacts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showFubImport, setShowFubImport] = useState(false);
  // Live count of contacts in Firestore while a sync is running. The
  // backend writes contacts in batches as it pages through Google, so a
  // Firestore listener gives us free per-second progress feedback without
  // any backend change.
  const [liveSyncCount, setLiveSyncCount] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const [firebaseSearch, setFirebaseSearch] = useState("");
  const [segment, setSegment] = useState("all");
  const [labelFilter, setLabelFilter] = useState("all"); // "all" or a groupResourceName
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const [labelQuery, setLabelQuery] = useState(""); // search inside the label menu
  const [availableLabels, setAvailableLabels] = useState([]);
  const [drawerContact, setDrawerContact] = useState(null);
  // null = closed; { mode: "create" } = add; { mode: "edit", contact } = edit
  const [editDrawer, setEditDrawer] = useState(null);
  const [editDrawerSaving, setEditDrawerSaving] = useState(false);

  const openCreate = () => setEditDrawer({ mode: "create" });
  const closeEditDrawer = () => {
    if (editDrawerSaving) return;
    setEditDrawer(null);
  };

  const handleDrawerSave = async (form) => {
    setEditDrawerSaving(true);
    try {
      if (!user?.gmailtoken?.access_token) {
        toast.error("Please connect Gmail first");
        return;
      }

      if (editDrawer?.mode === "create") {
        // Backend writes rich fields to Google + Firestore in one call.
        const created = await addContact({ user, contact: form });
        setFirebaseContacts((prev) => [
          { ...created, id: created.firebaseId, createdBy: user.id },
          ...prev,
        ]);
        toast.success("Contact added");
      } else if (editDrawer?.mode === "edit") {
        const contact = editDrawer.contact;
        const result = await updateContact({ user, contact, form });

        // Diff labels and sync any add/remove to Google.
        const prevLabels = new Set(
          (contact.labels || []).map((l) => l.groupResourceName),
        );
        const nextLabels = new Set(
          (form.labels || []).map((l) => l.groupResourceName),
        );
        const addGroupResourceNames = [...nextLabels].filter(
          (rn) => !prevLabels.has(rn),
        );
        const removeGroupResourceNames = [...prevLabels].filter(
          (rn) => !nextLabels.has(rn),
        );
        const resourceName =
          result.googleResourceName || contact.googleResourceName;
        if (
          resourceName &&
          (addGroupResourceNames.length > 0 ||
            removeGroupResourceNames.length > 0)
        ) {
          updateContactMemberships({
            userId: user.id,
            resourceName,
            contactId: contact.id,
            addGroupResourceNames,
            removeGroupResourceNames,
          }).catch((err) => {
            console.error("Label sync failed:", err);
            toast.warn("Saved, but couldn't sync labels to Google");
          });
        }

        setFirebaseContacts((prev) =>
          prev.map((c) => (c.id === contact.id ? { ...c, ...result } : c)),
        );
        toast.success("Contact updated");
      }

      setEditDrawer(null);
    } catch (err) {
      console.error("Contact save failed:", err);
      toast.error(err.message || "Save failed");
    } finally {
      setEditDrawerSaving(false);
    }
  };

  const handleContactUpdated = (id, patch) => {
    setFirebaseContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
    setDrawerContact((curr) =>
      curr && curr.id === id ? { ...curr, ...patch } : curr,
    );
  };

  // Attach CRM context to every synced contact: who they are to the business
  // (client / past client / lead), the record they're attached to, and when
  // something last happened. Deals and leads join on contactId, so this is an
  // exact match rather than name guessing.
  const enrichedContacts = useMemo(
    () => enrichContacts(firebaseContacts, { deals, buyerLeads, listingLeads }),
    [firebaseContacts, deals, buyerLeads, listingLeads],
  );

  const segmentCounts = useMemo(
    () => countBySegment(enrichedContacts),
    [enrichedContacts],
  );

  const filteredFirebaseContacts = useMemo(() => {
    const q = firebaseSearch.trim().toLowerCase();
    const normalizedSearch = q.replace(/\D/g, "");

    return filterBySegment(enrichedContacts, segment).filter((contact) => {
      // Label filter (independent of search).
      if (labelFilter !== "all") {
        const labels = Array.isArray(contact.labels) ? contact.labels : [];
        if (!labels.some((l) => l.groupResourceName === labelFilter)) {
          return false;
        }
      }

      if (!q) return true;

      const name = (contact.name || "").toLowerCase();
      const email = (contact.email || "").toLowerCase();
      const phone = (contact.phone || "").replace(/\D/g, "");

      if (normalizedSearch) {
        return phone.includes(normalizedSearch);
      }
      return name.includes(q) || email.includes(q);
    });
  }, [enrichedContacts, segment, firebaseSearch, labelFilter]);

  // Sort contacts alphabetically and group by first letter for A-Z section headers.
  // Names that don't start with a letter (numbers, symbols) bucket into "#".
  const groupedContacts = useMemo(() => {
    const sorted = [...filteredFirebaseContacts].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );
    const groups = new Map();
    for (const contact of sorted) {
      const firstChar = (contact.name || "").trim().charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(firstChar) ? firstChar : "#";
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(contact);
    }
    // Maps preserve insertion order; sorted input means letters appear in order.
    // Move "#" (non-letters) to the end if present.
    const entries = Array.from(groups.entries());
    const letterEntries = entries.filter(([k]) => k !== "#");
    const hashEntry = entries.find(([k]) => k === "#");
    return hashEntry ? [...letterEntries, hashEntry] : letterEntries;
  }, [filteredFirebaseContacts]);

  // replace useEffect fetchData
  useEffect(() => {
    if (user?.id) {
      loadFirebaseContacts();
      // Load available labels for the filter dropdown (cached in Firestore
      // by the backend's /list-contact-groups).
      loadContactGroupsFromFirestore(user.id).then(setAvailableLabels);
    }
  }, [user?.id]);

  // While a sync is running, watch sync_contacts via a Firestore snapshot
  // so the user sees the count tick up as the backend writes batches. The
  // listener detaches as soon as the sync finishes.
  useEffect(() => {
    if (!isSyncing || !user?.id) return;
    const q = query(
      collection(firestored, "sync_contacts"),
      where("createdBy", "==", user.id),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setLiveSyncCount(snap.size),
      (err) => console.warn("Live sync count listener failed:", err),
    );
    return () => unsub();
  }, [isSyncing, user?.id]);

  // reusable function
  const loadFirebaseContacts = async () => {
    setIsTableLoading(true);

    try {
      const data = await getAllContactsFromFirebase(user.id);
      setFirebaseContacts(data || []);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load contacts");
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;

    setSavingId(deleteItem.id);

    try {
      await deleteContactFromFirebase(deleteItem.id);

      const updated = firebaseContacts.filter(
        (item) => item.id !== deleteItem.id,
      );

      setFirebaseContacts(updated);

      toast.success("Contact deleted successfully!");
      setIsDeleting(false);
      setDeleteItem(null);
    } catch (err) {
      console.log(err);
      toast.error("Delete failed");
    }

    setSavingId(null);
  };

  const fetchContacts = async () => {
    setIsSyncing(true);

    try {
      const accessToken = user?.gmailtoken?.access_token;
      let effectiveSyncToken = user?.googleContactSync?.syncToken || null;

      if (!accessToken) {
        toast.error("Gmail disconnected!. Login first");
        return;
      }

      // Sync labels FIRST so /getcontacts has display names ready when it
      // resolves each contact's memberships. If the labels endpoint fails
      // we still let contacts sync, but surface the error so the user can
      // see why their labels aren't showing up.
      const labelsWereEmpty = availableLabels.length === 0;
      try {
        await syncContactGroupsFromGoogle(user.id);
        const refreshed = await loadContactGroupsFromFirestore(user.id);
        setAvailableLabels(refreshed);

        if (refreshed.length === 0) {
          // Endpoint succeeded but returned zero groups. Either the user
          // genuinely has no labels in Google, or the backend's filter is
          // dropping them. Tell the user so they can investigate.
          toast.info(
            "No labels found in your Google Contacts. Create one at contacts.google.com to use the filter.",
          );
        }

        // First time we ever synced labels? Force a full re-sync of
        // contacts so existing rows get their labels backfilled (delta
        // sync would skip unchanged contacts and leave labels blank).
        if (labelsWereEmpty && refreshed.length > 0 && effectiveSyncToken) {
          await updateUserProfile(user.id, {
            googleContactSync: {
              syncToken: null,
              lastSyncedAt: Date.now(),
            },
          });
          effectiveSyncToken = null;
        }
      } catch (err) {
        console.error("Label sync failed:", err);
        toast.warn(
          `Label sync failed: ${err.message || "unknown error"}. Contacts will still sync.`,
        );
      }

      const res = await authedFetch(
        // "http://localhost:3003/getcontacts",
            "https://us-central1-team-edelstein.cloudfunctions.net/api/getcontacts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: accessToken,
          syncToken: effectiveSyncToken,
          userId: user.id, // ✅ IMPORTANT ADD THIS
        }),
      });

      if (!res.ok) {
        // Not res.json(). A function that times out, runs out of memory, or
        // crashes never reaches our JSON error handler, and the platform
        // answers with plain-text "Internal Server Error". Parsing that threw,
        // and the SyntaxError was reported to the user instead of the outage.
        const err = await readErrorBody(res);

        // ⚠️ syncToken invalid case handle
        if (err.resetSync) {
          await updateUserProfile(user.id, {
            googleContactSync: {
              syncToken: null,
              lastSyncedAt: Date.now(),
            },
          });

          toast.info("Sync reset required. Please sync again.");
        } else {
          toast.error(`Syncing failed: ${err.error || `server error ${res.status}`}`);
        }

        return;
      }

      // const dataresponse = await res.json();

      // await updateUserProfile(user.id, {
      //   googleContactSync: {
      //     syncToken: dataresponse.nextSyncToken || savedSyncToken || null,
      //     lastSyncedAt: Date.now(),
      //   },
      // });

      const userInfo = await loadCurrentUser(user.id);

      dispatch({
        type: actionType.SET_USER,
        payload: userInfo,
      });

      loadFirebaseContacts();

      toast.success(
        effectiveSyncToken
          ? "New contacts synced successfully!"
          : "Contacts synced successfully!",
      );
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error(`Sync failed: ${error?.message || "unknown error"}`);
    } finally {
      setIsSyncing(false);
      setLiveSyncCount(0);
    }
  };

  // Follow Up Boss import is now a pick-and-choose flow handled by
  // FubImportModal (opened from the "Import from Follow Up Boss" button).

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Contacts"
        subtitle={`${firebaseContacts.length} synced`}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={fetchContacts}
              disabled={isSyncing}
              className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-md whitespace-nowrap"
            >
              {isSyncing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              {isSyncing
                ? liveSyncCount > 0
                  ? `Syncing… (${liveSyncCount} so far)`
                  : "Syncing…"
                : "Sync Contacts"}
            </button>
            <button
              onClick={() => setShowFubImport(true)}
              disabled={isSyncing}
              title="Pick which Follow Up Boss contacts to import"
              className="flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-sm font-semibold px-3 py-2 rounded-md whitespace-nowrap"
            >
              <Download size={14} />
              Import from Follow Up Boss
            </button>
          </div>
        }
      />

      <div className="overflow-x-auto w-[96%] mx-auto mt-10 bg-white rounded-2xl border border-gray-200 shadow-sm mb-10">
        <div className="flex flex-wrap justify-between items-center p-6 border-b">
          <div className="xl:w-[30%]">
            <h2 className="text-xl font-semibold">Synced Contacts</h2>
            {/* <p className="text-base font-semibold">Total Contact {filteredFirebaseContacts.length}</p> */}
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center sm:justify-end gap-3 w-full xl:w-[70%] mt-4 xl:mt-0">
            {/* Label filter — searchable dropdown (type to find a label
                instead of scrolling the whole list). */}
            <div className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setLabelMenuOpen((v) => !v);
                  setLabelQuery("");
                }}
                className="border px-3 py-2 rounded-lg outline-none text-sm bg-white flex items-center justify-between gap-2 w-full sm:w-auto sm:min-w-[170px] sm:max-w-[240px]"
                title={
                  availableLabels.length === 0
                    ? "Run Sync Contacts first to pull labels from Google"
                    : "Filter by label"
                }
              >
                <span className="truncate">
                  {labelFilter === "all"
                    ? availableLabels.length === 0
                      ? "All Labels (None Synced Yet)"
                      : "All Labels"
                    : availableLabels.find(
                        (g) => g.resourceName === labelFilter,
                      )?.name || "All Labels"}
                </span>
                <ChevronDown size={15} className="shrink-0 text-slate-500" />
              </button>
              {labelMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setLabelMenuOpen(false)}
                  />
                  <div className="absolute left-0 top-full mt-1 z-40 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                    <input
                      autoFocus
                      value={labelQuery}
                      onChange={(e) => setLabelQuery(e.target.value)}
                      placeholder="Search labels…"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-1 outline-none focus:border-slate-500"
                    />
                    <div className="max-h-[260px] overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setLabelFilter("all");
                          setLabelMenuOpen(false);
                        }}
                        className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 flex items-center justify-between"
                      >
                        <span>All Labels</span>
                        {labelFilter === "all" && (
                          <Check size={14} className="text-slate-700" />
                        )}
                      </button>
                      {availableLabels
                        .filter((g) =>
                          g.name
                            .toLowerCase()
                            .includes(labelQuery.trim().toLowerCase()),
                        )
                        .map((g) => (
                          <button
                            key={g.resourceName}
                            type="button"
                            onClick={() => {
                              setLabelFilter(g.resourceName);
                              setLabelMenuOpen(false);
                            }}
                            className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 flex items-center justify-between"
                          >
                            <span className="truncate">{g.name}</span>
                            {labelFilter === g.resourceName && (
                              <Check size={14} className="text-slate-700" />
                            )}
                          </button>
                        ))}
                      {availableLabels.length > 0 &&
                        availableLabels.filter((g) =>
                          g.name
                            .toLowerCase()
                            .includes(labelQuery.trim().toLowerCase()),
                        ).length === 0 && (
                          <p className="text-xs text-slate-400 px-2 py-1.5">
                            No labels match "{labelQuery}".
                          </p>
                        )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="w-full sm:flex-1 sm:w-auto sm:min-w-[160px] lg:max-w-[320px]">
              <input
                type="text"
                value={firebaseSearch}
                onChange={(e) => setFirebaseSearch(e.target.value)}
                placeholder="Search synced contacts..."
                className="border px-4 py-2 rounded-lg w-full outline-none"
              />
            </div>
            <div className="flex gap-3 items-center w-full sm:w-auto">
              {gmailConnected ? (
                <button
                  onClick={openCreate}
                  className="bg-slate-800 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto whitespace-nowrap"
                >
                  + Add Contact
                </button>
              ) : (
                <button
                  onClick={() =>
                    navigate("/dashboard/settings?tab=gmailConnection")
                  }
                  title="Connect Gmail to add contacts"
                  className="inline-flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-semibold w-full sm:w-auto whitespace-nowrap"
                >
                  <Mail size={14} />
                  Connect Gmail to add contacts
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 px-4 pb-4">
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSegment(s.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                segment === s.id
                  ? "bg-[#1E293B] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s.label}
              <span
                className={`ml-1.5 text-xs ${
                  segment === s.id ? "text-white/70" : "text-gray-400"
                }`}
              >
                {segmentCounts[s.id] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <table className="min-w-[700px] w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-center text-gray-500 text-xs uppercase font-semibold">
                Name
              </th>
              <th className="px-4 py-3 text-center text-gray-500 text-xs uppercase font-semibold">
                Phone
              </th>
              <th className="px-4 py-3 text-center text-gray-500 text-xs uppercase font-semibold">
                Email
              </th>
              <th className="px-4 py-3 text-center text-gray-500 text-xs uppercase font-semibold">
                Status
              </th>
              <th className="px-2 py-3 w-10" />
            </tr>
          </thead>

          <tbody>
            {isTableLoading ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonRow key={i} cols={5} />
                ))}
              </>
            ) : (
              <>
                {filteredFirebaseContacts.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center font-semibold py-10 text-gray-500"
                    >
                      No contact added yet
                    </td>
                  </tr>
                ) : (
                  groupedContacts.map(([letter, contacts]) => (
                    <Fragment key={letter}>
                      <tr className="bg-slate-100">
                        <td
                          colSpan="5"
                          className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-600"
                        >
                          {letter}
                        </td>
                      </tr>
                      {contacts.map((item, i) => (
                    <tr
                      key={item.id || `${letter}-${i}`}
                      onClick={() => setDrawerContact(item)}
                      className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-center">{item.name}</td>
                      <td className="px-4 py-3 text-center">{item.phone}</td>
                      <td className="px-4 py-3 text-center">
                        {item.email || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                            SEGMENT_BADGE[item.segment] || SEGMENT_BADGE.other
                          }`}
                        >
                          {SEGMENT_LABEL[item.segment] || "-"}
                        </span>
                        {item.link && (
                          <div className="text-[11px] text-gray-500 truncate max-w-[200px] mx-auto mt-1">
                            {item.link.label}
                          </div>
                        )}
                        {item.lastActivityAt > 0 && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {formatDate(item.lastActivityAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3 text-center w-10">
                        <button
                          type="button"
                          title="Delete contact"
                          aria-label={`Delete ${item.name || "contact"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteItem(item);
                            setIsDeleting(true);
                          }}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                      ))}
                    </Fragment>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {isDeleting && (
        <DeleteRow
          loading={savingId === deleteItem?.id}
          onClose={() => {
            setIsDeleting(false);
            setDeleteItem(null);
          }}
          handleYes={handleDelete}
        />
      )}
      {drawerContact && (
        <ContactDrawer
          contact={drawerContact}
          deals={deals}
          onClose={() => setDrawerContact(null)}
          onContactUpdated={handleContactUpdated}
        />
      )}
      {editDrawer && (
        <ContactEditDrawer
          initial={editDrawer.mode === "edit" ? editDrawer.contact : null}
          onClose={closeEditDrawer}
          onSubmit={handleDrawerSave}
          saving={editDrawerSaving}
        />
      )}
      {showFubImport && (
        <FubImportModal
          userId={user?.id}
          onClose={() => setShowFubImport(false)}
          onImported={loadFirebaseContacts}
        />
      )}
    </div>
  );
}

export default MainNewContact;
